import { useMsal } from '@azure/msal-react'
import { useState, useEffect } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import IframePage from 'src/pages/IframePage/IframePage'
import { initIntercomVisitor, identifyIntercomUser } from 'src/services/intercom'

import { ProtectedRoute } from './ProtectedRoute'
import { RoleRoute } from './RoleRoute'
import { initializeGTM } from '../analytics/gtm'
import { useGtmPageTracking } from '../analytics/usePageTracking'
import { DASHBOARD_ROLES } from '../auth/roles'
import AuthCallback from '../components/AuthCallback'
import { Header } from '../components/header/Header'
import MainContent from '../components/MainContent'
import Navbar from '../components/navBar/NavBar'
import NotFoundPage from '../components/NotFoundPage'
import ProfilePage from '../components/profile/ProfilePage'
import Login from '../pages/Login/LoginPage'
import { ApiProvider } from '../providers/ApiProvider'
import { QueryProvider } from '../providers/QueryProvider'
import { useUserQuery } from '../queries'

const GtmTracker = () => {
  useGtmPageTracking()
  return null
}
export function App() {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { accounts } = useMsal()

  const toggleSidebar = (): void => {
    setIsOpen(prev => !prev)
  }

  const isLoggedIn = accounts.length > 0

  useEffect(() => {
    initializeGTM()
  }, [])

  useEffect(() => {
    if (!isLoggedIn) {
      initIntercomVisitor()
    }
  }, [isLoggedIn])

  return (
    <QueryProvider>
      <ApiProvider>
        <BrowserRouter>
          <GtmTracker />
          <AppContent toggleSidebar={toggleSidebar} isOpen={isOpen} isLoggedIn={isLoggedIn} />
        </BrowserRouter>
      </ApiProvider>
    </QueryProvider>
  )
}

interface AppContentProps {
  toggleSidebar: () => void
  isOpen: boolean
  isLoggedIn: boolean
}

const AppContent = ({ toggleSidebar, isOpen, isLoggedIn }: AppContentProps) => {
  const { data: userData } = useUserQuery()

  useEffect(() => {
    if (isLoggedIn && userData) {
      void identifyIntercomUser(userData)
    }
  }, [isLoggedIn, userData])

  return (
    <>
      {isLoggedIn && <Header toggleSidebar={toggleSidebar} userData={userData} />}
      {/* Overlay with blur */}
      {isLoggedIn && (
        <div
          className={`fixed inset-0 transition-all duration-300 backdrop-blur-sm bg-black/20 menu-overlay
              ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      {isLoggedIn && <Navbar isOpen={isOpen} onClose={toggleSidebar} />}

      <main>
        <Routes>
          <Route
            path="/faqs"
            element={
              <ProtectedRoute>
                <div id="faq-microfrontend" />
              </ProtectedRoute>
            }
          />
          <Route path="/auth-callback" element={<AuthCallback />} />
          <Route path="/login" element={<Login />} />
          <Route path="/HomePage" element={<MainContent userData={userData} />} />
          <Route path="/" element={<MainContent userData={userData} />} />
          <Route
            path="/ideas-bank/*"
            element={
              <ProtectedRoute>
                <div id="ideas-bank" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/experience-profile/*"
            element={
              <ProtectedRoute>
                <div id="experience-profile" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/our-work/*"
            element={
              <ProtectedRoute>
                <div id="our-work" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing-capabilities/*"
            element={
              <ProtectedRoute>
                <div id="marketing-capabilities" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prioritization-process/*"
            element={
              <ProtectedRoute>
                <div id="prioritization-process" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/innovation-process/*"
            element={
              <ProtectedRoute>
                <div id="innovation-process" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/*"
            element={
              <ProtectedRoute>
                <div id="onboarding" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/experience-loop-process/*"
            element={
              <ProtectedRoute>
                <div id="experience-loop-process" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/persistent-platform/*"
            element={
              <ProtectedRoute>
                <div id="persistent-platform" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools-and-support/*"
            element={
              <ProtectedRoute>
                <div id="tools-and-support" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/communities-of-practitioners/*"
            element={
              <ProtectedRoute>
                <div id="communities-of-practitioners" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency-roster/*"
            element={
              <ProtectedRoute>
                <div id="agency-roster" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage userData={userData} />
              </ProtectedRoute>
            }
          />

          {/* Iframe routes */}
          <Route
            path="/innovation-pipeline"
            element={
              <ProtectedRoute>
                <IframePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/innovation-effectiveness"
            element={
              <ProtectedRoute>
                <IframePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ideation-fast-start"
            element={
              <ProtectedRoute>
                <IframePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mdl-dashboards"
            element={
              <ProtectedRoute>
                <IframePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/media-modeling-engine"
            element={
              <ProtectedRoute>
                <IframePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/media-data-ai"
            element={
              <ProtectedRoute>
                <IframePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/media-data-ai-questions"
            element={
              <ProtectedRoute>
                <IframePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/nma-home"
            element={
              <ProtectedRoute>
                <div id="nma-home"> </div>
              </ProtectedRoute>
            }
          />

          <Route
            path="/nma-2025-ceremony"
            element={
              <ProtectedRoute>
                <IframePage url="https://vimeo.com/event/5515704/embed" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/nma-gallery"
            element={
              <ProtectedRoute>
                <div id="nma-gallery"> </div>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bva/*"
            element={
              <ProtectedRoute>
                <div id="bva"> </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/page-dashboard"
            element={
              <ProtectedRoute>
                <RoleRoute roles={DASHBOARD_ROLES}>
                  <div id="marketing-capabilities"> </div>
                </RoleRoute>
              </ProtectedRoute>
            }
          />

          {/* Keep catch-all LAST and AFTER /ideas/* */}
          <Route path="*" element={<NotFoundPage />} />
          <Route path="/not-found" element={<NotFoundPage />} />
        </Routes>
      </main>
    </>
  )
}
